import { useEffect, useState } from "react";
import { UsersFour, Plus, PencilSimple, Trash, X, LinkSimple, LockSimple, Ticket, CalendarPlus, ArrowSquareOut, CheckCircle, Prohibit, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { AttendanceToggle, NoShowBadge, AttendanceSummary } from "../../components/AttendanceToggle";
import GroupMigrationSection from "./GroupMigrationSection";
import GroupReviewsSection from "./GroupReviewsSection";
import GroupPaymentsSection from "./GroupPaymentsSection";

const SYM = { usd: "$", gbp: "£", eur: "€", cad: "$", aud: "$", nzd: "$", inr: "₹", jpy: "¥" };
const money = (n, c) => `${SYM[(c || "gbp").toLowerCase()] || ""}${Number(n || 0).toFixed(2)}`;
const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_e) { return iso; }
};

export default function TrainerGroupSessions() {
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("classes");

  const loadStatus = async (refresh = false) => {
    try {
      const { data } = await api.get(`/group/addon/status${refresh ? "?refresh=true" : ""}`);
      setStatus(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromCheckout = params.has("addon_session");
    loadStatus(fromCheckout).then(() => {
      if (fromCheckout) window.history.replaceState({}, "", window.location.pathname);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!status) return <div className="card-flat p-16 text-center text-zinc-500 tracker">Loading…</div>;
  if (!status.active) return <AddonUpsell status={status} />;

  return (
    <div className="space-y-8" data-testid="trainer-group-sessions">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ Group Sessions ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Group.</h1>
          <p className="text-zinc-400 mt-3">Public class schedule — anyone can book a spot, clients and non-clients alike.</p>
        </div>
        <PublicLinkBar url={status.public_url} standalone={status.plan === "standalone"} />
      </div>

      <div className="flex gap-1 border-b border-white/10">
        {[["classes", "Classes & bookings"], ["packs", "Class packs"], ["reviews", "Reviews"], ["payments", "Payments"], ["migrate", "Move my clients over"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`tracker px-4 py-2.5 ${tab === k ? "text-white border-b-2 border-brand" : "text-zinc-500"}`}
            data-testid={`group-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "classes" ? <ClassesSection currency={status.trainer_currency} /> : tab === "packs" ? <PacksSection currency={status.trainer_currency} /> : tab === "reviews" ? <GroupReviewsSection /> : tab === "payments" ? <GroupPaymentsSection /> : <GroupMigrationSection />}
    </div>
  );
}

function AddonUpsell({ status }) {
  const [loading, setLoading] = useState(false);
  const unlock = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/group/addon/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };
  return (
    <div className="card-flat p-10 sm:p-16 max-w-2xl" data-testid="group-addon-upsell">
      <LockSimple size={32} weight="duotone" className="text-brand mb-4" />
      <div className="tracker text-brand mb-3">[ Add-on ]</div>
      <h1 className="font-display text-4xl mb-3">Group Sessions.</h1>
      <p className="text-zinc-400 mb-6">A Mindbody-style public booking calendar — completely separate from your client list.</p>
      <ul className="space-y-2 text-sm text-zinc-300 mb-8">
        <li>• Create group classes with capacity limits and per-head pricing</li>
        <li>• Anyone books via your public schedule page — no account needed</li>
        <li>• Every booking paid upfront through Stripe into your connected account</li>
        <li>• Sell class packs — buyers get a redeemable pack code</li>
      </ul>
      <div className="flex items-center gap-4 flex-wrap">
        <button onClick={unlock} disabled={loading} className="btn-primary" data-testid="group-addon-unlock-btn">
          {loading ? "Redirecting…" : `Unlock for £${(status.addon_price / 100).toFixed(0)}/month`}
        </button>
        <span className="tracker text-zinc-500 text-[10px]">Added to your existing Traina subscription</span>
      </div>
    </div>
  );
}

function PublicLinkBar({ url, standalone }) {
  const full = url?.startsWith("http") ? url : `${window.location.origin}${url}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      toast.success("Public schedule link copied");
    } catch (_e) { toast.error("Couldn't copy"); }
  };
  const manage = async () => {
    try {
      const { data } = await api.post("/platform/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open billing portal"); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {standalone && (
        <button onClick={manage} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="group-manage-billing">
          Manage billing
        </button>
      )}
      <button onClick={copy} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="group-copy-link">
        <LinkSimple size={14} weight="bold" /> Copy public link
      </button>
      <a href={full} target="_blank" rel="noopener noreferrer" className="btn-primary !py-2 !px-3 !text-xs" data-testid="group-open-public">
        <ArrowSquareOut size={14} weight="bold" /> View schedule
      </a>
    </div>
  );
}

/* ---------------- classes ---------------- */

function ClassesSection({ currency }) {
  const [classes, setClasses] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [showPast, setShowPast] = useState(false);

  const load = () => api.get("/group/classes").then(({ data }) => setClasses(data)).catch(() => setClasses([]));
  useEffect(() => { load(); }, []);

  const archive = async (c) => {
    if (!window.confirm(`Archive "${c.title}"? Existing bookings are kept.`)) return;
    try {
      await api.delete(`/group/classes/${c.id}`);
      toast.success("Class archived");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!classes) return <div className="card-flat p-16 text-center text-zinc-500 tracker">Loading…</div>;

  const now = new Date();
  const upcoming = classes.filter((c) => new Date(c.start) >= now);
  const past = classes.filter((c) => new Date(c.start) < now);
  const shown = showPast ? past : upcoming;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1">
          <button onClick={() => setShowPast(false)} className={`tracker px-3 py-1.5 border ${!showPast ? "border-brand text-white" : "border-white/10 text-zinc-500"}`} data-testid="group-filter-upcoming">Upcoming ({upcoming.length})</button>
          <button onClick={() => setShowPast(true)} className={`tracker px-3 py-1.5 border ${showPast ? "border-brand text-white" : "border-white/10 text-zinc-500"}`} data-testid="group-filter-past">Past ({past.length})</button>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary" data-testid="group-new-class-btn">
          <Plus size={16} weight="bold" /> New class
        </button>
      </div>

      {shown.length === 0 ? (
        <div className="card-flat p-16 text-center" data-testid="group-classes-empty">
          <CalendarPlus size={32} weight="duotone" className="text-brand mx-auto mb-3" />
          <div className="font-display text-2xl mb-2">{showPast ? "No past classes." : "No classes yet."}</div>
          {!showPast && <div className="text-zinc-500 tracker text-[10px]">Create your first group class — it shows on your public schedule instantly.</div>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="group-classes-grid">
          {shown.map((c) => (
            <div key={c.id} className="card-flat p-5 flex flex-col" data-testid={`group-class-card-${c.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-display text-xl leading-tight truncate">{c.title}</div>
                  <div className="tracker text-zinc-500 text-[10px] mt-1">{fmtWhen(c.start)} · {c.duration_min} min</div>
                  {c.location && <div className="tracker text-zinc-500 text-[10px]">{c.location}</div>}
                  <div className={`tracker text-[10px] mt-1 ${c.instructor_name ? "text-brand-light" : "text-amber-400"}`} data-testid={`group-class-instructor-${c.id}`}>
                    {c.instructor_name ? `with ${c.instructor_name}` : "No instructor assigned"}
                    {c.instructor_name && <span className="text-zinc-500"> · pays {money(c.instructor_pay, c.currency)}{c.payroll_run_id ? " · paid out" : ""}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => { setEditing(c); setShowForm(true); }} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`group-edit-class-${c.id}`}><PencilSimple size={12} weight="bold" /></button>
                  <button onClick={() => archive(c)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`group-archive-class-${c.id}`}><Trash size={12} weight="bold" /></button>
                </div>
              </div>
              <div className="flex items-end justify-between mt-auto pt-3">
                <div className="font-display text-2xl money">{money(c.price, c.currency)}</div>
                <span className={`tracker text-[9px] px-2 py-0.5 border ${c.spots_left === 0 ? "border-red-500/40 text-red-400" : "border-emerald-500/40 text-emerald-400"}`} data-testid={`group-class-spots-${c.id}`}>
                  {c.booked_count}/{c.capacity} BOOKED{c.spots_left === 0 ? " · FULL" : ""}{c.waitlist_count ? ` · ${c.waitlist_count} WAITING` : ""}
                </span>
              </div>
              <AttendanceSummary cls={c} testId={`group-class-attendance-${c.id}`} />
              <button onClick={() => setViewing(c)} className="btn-ghost justify-center mt-3 !text-xs" data-testid={`group-view-bookings-${c.id}`}>
                View bookings ({c.booked_count})
              </button>
            </div>
          ))}
        </div>
      )}

      {showForm && <ClassForm editing={editing} currency={currency} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
      {viewing && <BookingsModal cls={classes.find((c) => c.id === viewing.id) || viewing} onClose={() => setViewing(null)} onChanged={load} />}
    </div>
  );
}

function ClassForm({ editing, currency, onClose, onDone }) {
  const [form, setForm] = useState(() => ({
    title: editing?.title || "",
    description: editing?.description || "",
    start: editing?.start || "",
    duration_min: editing?.duration_min || 60,
    location: editing?.location || "",
    capacity: editing?.capacity || 10,
    price: editing?.price ?? 10,
    currency: editing?.currency || currency || "gbp",
    stripe_link: editing?.stripe_link || "",
    instructor_id: editing?.instructor_id || "",
    instructor_pay: editing?.instructor_pay ?? "",
  }));
  const [loading, setLoading] = useState(false);
  const [staff, setStaff] = useState([]);
  useEffect(() => { api.get("/staff").then(({ data }) => setStaff(data.filter((s) => s.active !== false))).catch(() => setStaff([])); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setInstructor = (e) => {
    const id = e.target.value;
    const s = staff.find((x) => x.id === id);
    setForm({ ...form, instructor_id: id, instructor_pay: s ? (s.rates?.["Group Session"] ?? "") : "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form, duration_min: Number(form.duration_min), capacity: Number(form.capacity), price: Number(form.price),
        instructor_pay: form.instructor_id && form.instructor_pay !== "" ? Number(form.instructor_pay) : null,
      };
      if (editing) {
        await api.patch(`/group/classes/${editing.id}`, payload);
        toast.success("Class updated");
      } else {
        await api.post("/group/classes", payload);
        toast.success("Class created — it's live on your public schedule");
      }
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card-flat w-full max-w-lg p-8 my-8" onClick={(e) => e.stopPropagation()} data-testid="group-class-form">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ {editing ? "Edit" : "New"} class ]</div>
            <h2 className="font-display text-3xl">{editing ? "Update." : "Schedule."}</h2>
          </div>
          <button onClick={onClose}><X size={20} weight="bold" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tracker block mb-2">Class title</label>
            <input required value={form.title} onChange={set("title")} className="w-full px-4 py-3 border" placeholder="Saturday Bootcamp" data-testid="group-class-title" />
          </div>
          <div>
            <label className="tracker block mb-2">Description</label>
            <textarea rows={2} value={form.description} onChange={set("description")} className="w-full px-4 py-3 border" data-testid="group-class-description" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="tracker block mb-2">Date & time</label>
              <input required type="datetime-local" value={form.start} onChange={set("start")} className="w-full px-4 py-3 border" data-testid="group-class-start" />
            </div>
            <div>
              <label className="tracker block mb-2">Duration (min)</label>
              <input required type="number" min="10" max="480" value={form.duration_min} onChange={set("duration_min")} className="w-full px-4 py-3 border" data-testid="group-class-duration" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="tracker block mb-2">Location</label>
              <input value={form.location} onChange={set("location")} className="w-full px-4 py-3 border" placeholder="Studio 1, Main St" data-testid="group-class-location" />
            </div>
            <div>
              <label className="tracker block mb-2">Capacity (spots)</label>
              <input required type="number" min="1" max="500" value={form.capacity} onChange={set("capacity")} className="w-full px-4 py-3 border" data-testid="group-class-capacity" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="tracker block mb-2">Price per head</label>
              <input required type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className="w-full px-4 py-3 border" data-testid="group-class-price" />
            </div>
            <div>
              <label className="tracker block mb-2">Currency</label>
              <select value={form.currency} onChange={set("currency")} className="w-full px-4 py-3 border" data-testid="group-class-currency">
                <option value="gbp">GBP</option><option value="usd">USD</option><option value="eur">EUR</option>
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-[1fr_140px] gap-3">
            <div>
              <label className="tracker block mb-2">Instructor</label>
              <select value={form.instructor_id} onChange={setInstructor} disabled={Boolean(editing?.payroll_run_id)} className="w-full px-4 py-3 border" data-testid="group-class-instructor">
                <option value="">Unassigned — let an instructor pick it up</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name}{s.has_login ? " · has login" : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="tracker block mb-2">Instructor pay</label>
              <input type="number" min="0" step="0.01" value={form.instructor_pay} onChange={set("instructor_pay")} disabled={!form.instructor_id || Boolean(editing?.payroll_run_id)} className="w-full px-4 py-3 border disabled:opacity-40" placeholder="—" data-testid="group-class-instructor-pay" />
            </div>
          </div>
          <p className="tracker text-zinc-500 text-[9px] -mt-2">
            {editing?.payroll_run_id ? "Locked — this class is already in a payroll run." : "Pay defaults to their Group Session rate (Payroll → Staff) and flows into their payroll run once the class has happened."}
          </p>
          <p className="tracker text-zinc-500 text-[9px] -mt-1">Every spot is paid upfront by card (secure Stripe checkout into your connected account) or with a class pack code.</p>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="group-class-submit">
            {loading ? "Saving…" : editing ? "Update class" : "Create class"}
          </button>
        </form>
      </div>
    </div>
  );
}

export function BookingsModal({ cls, onClose, onChanged }) {
  const [data, setData] = useState(null);

  const load = () => api.get(`/group/classes/${cls.id}/bookings`).then(({ data: d }) => setData(d)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cls.id]);

  const act = async (b, action) => {
    try {
      await api.post(`/group/bookings/${b.id}/${action}`);
      toast.success(action === "mark-paid" ? `${b.name} marked paid` : `${b.name}'s booking cancelled`);
      load();
      onChanged();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const removeWait = async (w) => {
    try {
      await api.delete(`/group/waitlist/${w.id}`);
      toast.success(`${w.name} removed from waitlist`);
      load();
      onChanged();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [sendingReminder, setSendingReminder] = useState(false);
  const sendReminders = async () => {
    setSendingReminder(true);
    try {
      const { data: r } = await api.post(`/group/classes/${cls.id}/send-reminders`);
      toast.success(r.sent ? `Reminder sent to ${r.sent} attendee${r.sent === 1 ? "" : "s"}` : "No active bookings to remind");
      onChanged();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSendingReminder(false); }
  };

  const bookings = data?.bookings || [];
  const waitlist = data?.waitlist || [];
  const activeCount = bookings.filter((b) => b.status !== "cancelled").length;
  const upcoming = new Date(cls.start) > new Date();
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card-flat w-full max-w-2xl p-8 my-8" onClick={(e) => e.stopPropagation()} data-testid="group-bookings-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="tracker text-brand mb-2">[ Bookings ]</div>
            <h2 className="font-display text-2xl">{cls.title}</h2>
            <div className="tracker text-zinc-500 text-[10px] mt-1">{fmtWhen(cls.start)}</div>
            <div className="tracker text-[9px] mt-1.5 text-zinc-500" data-testid="group-reminder-status">
              {cls.reminder_sent_at
                ? `Reminder emailed ${new Date(cls.reminder_sent_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}${cls.reminder_count != null ? ` · ${cls.reminder_count} sent` : ""}`
                : upcoming ? "24-hour reminder goes out automatically to everyone booked" : ""}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {upcoming && activeCount > 0 && (
              <button onClick={sendReminders} disabled={sendingReminder} className="btn-ghost !py-1.5 !px-3 !text-[10px]" title="Email everyone booked a branded reminder now" data-testid="group-send-reminders">
                <EnvelopeSimple size={12} weight="bold" /> {sendingReminder ? "Sending…" : cls.reminder_sent_at ? "Send again" : "Send reminder now"}
              </button>
            )}
            <button onClick={onClose}><X size={20} weight="bold" /></button>
          </div>
        </div>
        {bookings.length === 0 ? (
          <p className="text-zinc-500 text-sm py-8 text-center" data-testid="group-bookings-empty">No bookings yet — share your public link to fill this class.</p>
        ) : (
          <div className="space-y-2">
            {bookings.map((b) => (
              <div key={b.id} className={`flex items-center justify-between gap-3 border px-4 py-3 flex-wrap ${b.status === "cancelled" ? "border-white/5 opacity-50" : "border-white/10 bg-zinc-900/40"}`} data-testid={`group-booking-row-${b.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200 truncate flex items-center gap-2">{b.name} <NoShowBadge booking={b} testPrefix="group-noshows" /></div>
                  <div className="tracker text-[9px] text-zinc-500 flex items-center gap-1.5">
                    <EnvelopeSimple size={10} /> {b.email}{b.phone ? ` · ${b.phone}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {b.status !== "cancelled" && <AttendanceToggle booking={b} endpoint="/group/bookings" onChanged={() => { load(); onChanged(); }} testPrefix="group-attendance" />}
                  <span className={`tracker text-[9px] px-2 py-0.5 border ${b.status === "paid" ? "border-emerald-500/40 text-emerald-400" : b.status === "cancelled" ? "border-zinc-700 text-zinc-500" : "border-amber-500/40 text-amber-400"}`}>
                    {b.status === "paid" ? `PAID${b.paid_via ? ` · ${b.paid_via.replace("_", " ").toUpperCase()}` : ""}` : b.status === "awaiting_payment" ? "HOLD · AWAITING PAYMENT" : b.status.toUpperCase()}
                  </span>
                  {(b.status === "pending" || b.status === "awaiting_payment") && (
                    <button onClick={() => act(b, "mark-paid")} className="btn-ghost !py-1 !px-2 !text-[10px]" title="Mark paid" data-testid={`group-mark-paid-${b.id}`}>
                      <CheckCircle size={13} weight="bold" className="text-emerald-400" />
                    </button>
                  )}
                  {b.status !== "cancelled" && (
                    <button onClick={() => act(b, "cancel")} className="btn-ghost !py-1 !px-2 !text-[10px]" title="Cancel booking" data-testid={`group-cancel-booking-${b.id}`}>
                      <Prohibit size={13} weight="bold" className="text-red-400" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {waitlist.length > 0 && (
          <div className="mt-6" data-testid="group-waitlist-section">
            <div className="tracker text-amber-400 text-[10px] mb-2">WAITLIST ({waitlist.length}) — next in line is booked in automatically when a spot frees up</div>
            <div className="space-y-2">
              {waitlist.map((w, i) => (
                <div key={w.id} className="flex items-center justify-between gap-3 border border-amber-500/20 px-4 py-2.5" data-testid={`group-waitlist-row-${w.id}`}>
                  <div className="min-w-0 flex items-center gap-3">
                    <span className="font-display text-lg text-zinc-500">#{i + 1}</span>
                    <div>
                      <div className="text-sm text-zinc-200 truncate">{w.name}</div>
                      <div className="tracker text-[9px] text-zinc-500 flex items-center gap-1.5"><EnvelopeSimple size={10} /> {w.email}{w.phone ? ` · ${w.phone}` : ""}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="tracker text-[9px] px-2 py-0.5 border border-amber-500/40 text-amber-400">WAITING</span>
                    <button onClick={() => removeWait(w)} className="btn-ghost !py-1 !px-2 !text-[10px]" title="Remove from waitlist" data-testid={`group-waitlist-remove-${w.id}`}>
                      <Prohibit size={13} weight="bold" className="text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- packs ---------------- */

function PacksSection({ currency }) {
  const [packs, setPacks] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [p, pu] = await Promise.all([api.get("/group/packs"), api.get("/group/pack-purchases")]);
      setPacks(p.data);
      setPurchases(pu.data);
    } catch (_e) { setPacks([]); }
  };
  useEffect(() => { load(); }, []);

  const archive = async (p) => {
    if (!window.confirm(`Archive "${p.name}"? Existing codes keep working.`)) return;
    try { await api.delete(`/group/packs/${p.id}`); toast.success("Pack archived"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const markPaid = async (pu) => {
    try {
      const { data } = await api.post(`/group/pack-purchases/${pu.id}/mark-paid`);
      toast.success(`Code ${data.code} generated & emailed to ${pu.email}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const cancelPurchase = async (pu) => {
    try { await api.post(`/group/pack-purchases/${pu.id}/cancel`); toast.success("Purchase cancelled"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!packs) return <div className="card-flat p-16 text-center text-zinc-500 tracker">Loading…</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-zinc-400 text-sm">Sell bundles of class credits — buyers pay via your Stripe link and get a redeemable code.</p>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary" data-testid="group-new-pack-btn">
          <Plus size={16} weight="bold" /> New pack
        </button>
      </div>

      {packs.length === 0 ? (
        <div className="card-flat p-12 text-center" data-testid="group-packs-empty">
          <Ticket size={32} weight="duotone" className="text-brand mx-auto mb-3" />
          <div className="font-display text-2xl mb-2">No packs yet.</div>
          <div className="text-zinc-500 tracker text-[10px]">e.g. "10-Class Pack — £80" · shows on your public schedule.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="group-packs-grid">
          {packs.map((p) => (
            <div key={p.id} className="card-flat p-5 flex flex-col" data-testid={`group-pack-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-display text-xl leading-tight truncate">{p.name}</div>
                  <div className="tracker text-zinc-500 text-[10px] mt-1">{p.credits} class credits</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => { setEditing(p); setShowForm(true); }} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`group-edit-pack-${p.id}`}><PencilSimple size={12} weight="bold" /></button>
                  <button onClick={() => archive(p)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`group-archive-pack-${p.id}`}><Trash size={12} weight="bold" /></button>
                </div>
              </div>
              <div className="font-display text-2xl money mt-auto">{money(p.price, p.currency)}</div>
            </div>
          ))}
        </div>
      )}

      <div>
        <h3 className="font-display text-2xl mb-4">Pack purchases</h3>
        {purchases.length === 0 ? (
          <p className="text-zinc-500 text-sm" data-testid="group-purchases-empty">No purchases yet.</p>
        ) : (
          <div className="space-y-2" data-testid="group-purchases-list">
            {purchases.map((pu) => (
              <div key={pu.id} className={`flex items-center justify-between gap-3 border px-4 py-3 flex-wrap ${pu.status === "cancelled" ? "border-white/5 opacity-50" : "border-white/10 bg-zinc-900/40"}`} data-testid={`group-purchase-row-${pu.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200">{pu.name} · <span className="text-zinc-400">{pu.pack_name}</span></div>
                  <div className="tracker text-[9px] text-zinc-500">{pu.email} · {money(pu.price, pu.currency)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {pu.status === "paid" ? (
                    <span className="tracker text-[9px] px-2 py-0.5 border border-emerald-500/40 text-emerald-400" data-testid={`group-purchase-code-${pu.id}`}>
                      {pu.code} · {pu.credits_left}/{pu.credits} LEFT
                    </span>
                  ) : pu.status === "cancelled" ? (
                    <span className="tracker text-[9px] px-2 py-0.5 border border-zinc-700 text-zinc-500">CANCELLED</span>
                  ) : (
                    <>
                      <span className="tracker text-[9px] px-2 py-0.5 border border-amber-500/40 text-amber-400">AWAITING PAYMENT</span>
                      <button onClick={() => markPaid(pu)} className="btn-primary !py-1.5 !px-3 !text-[10px]" data-testid={`group-purchase-mark-paid-${pu.id}`}>
                        Mark paid & send code
                      </button>
                      <button onClick={() => cancelPurchase(pu)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`group-purchase-cancel-${pu.id}`}>
                        <Prohibit size={12} weight="bold" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && <PackForm editing={editing} currency={currency} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function PackForm({ editing, currency, onClose, onDone }) {
  const [form, setForm] = useState(() => ({
    name: editing?.name || "",
    description: editing?.description || "",
    credits: editing?.credits || 10,
    price: editing?.price ?? 80,
    currency: editing?.currency || currency || "gbp",
    stripe_link: editing?.stripe_link || "",
  }));
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, credits: Number(form.credits), price: Number(form.price) };
      if (editing) {
        await api.patch(`/group/packs/${editing.id}`, payload);
        toast.success("Pack updated");
      } else {
        await api.post("/group/packs", payload);
        toast.success("Pack created");
      }
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-flat w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()} data-testid="group-pack-form">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ {editing ? "Edit" : "New"} pack ]</div>
            <h2 className="font-display text-3xl">{editing ? "Update." : "Bundle."}</h2>
          </div>
          <button onClick={onClose}><X size={20} weight="bold" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tracker block mb-2">Pack name</label>
            <input required value={form.name} onChange={set("name")} className="w-full px-4 py-3 border" placeholder="10-Class Pack" data-testid="group-pack-name" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="tracker block mb-2">Credits</label>
              <input required type="number" min="1" max="200" value={form.credits} onChange={set("credits")} className="w-full px-4 py-3 border" data-testid="group-pack-credits" />
            </div>
            <div>
              <label className="tracker block mb-2">Price</label>
              <input required type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className="w-full px-4 py-3 border" data-testid="group-pack-price" />
            </div>
            <div>
              <label className="tracker block mb-2">Currency</label>
              <select value={form.currency} onChange={set("currency")} className="w-full px-4 py-3 border" data-testid="group-pack-currency">
                <option value="gbp">GBP</option><option value="usd">USD</option><option value="eur">EUR</option>
              </select>
            </div>
          </div>
          <p className="tracker text-zinc-500 text-[9px] -mt-1">Buyers pay by card through Stripe checkout — the pack code is emailed automatically the moment payment lands.</p>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="group-pack-submit">
            {loading ? "Saving…" : editing ? "Update pack" : "Create pack"}
          </button>
        </form>
      </div>
    </div>
  );
}
